import service from './http'
import Plain from "@/types/Plain";
import mountQuery from "@/utils/mountQuery";
import serve from "@/utils/serve";

type SystemIdeaAlias = { systemAlias: string, ideaAlias: string }
type SystemIdeaInstanceAlias = { systemAlias: string, ideaAlias: string, instanceId: number }
/**
 * O que uma leitura de instância pede. Viaja INTEIRA para o mountQuery, como o IdeaService já
 * faz: desmontá-la nos métodos obrigava a tocar nos DOIS a cada campo novo.
 */
type Config = {
    meta?: Array<string>
    options?: Array<string>
    filter?: Plain
    /**
     * Até que nível os relacionamentos vêm resolvidos por inteiro. Ausente é 1, que resolve os
     * do item lido; passado esse nível o servidor resolve só o que compõe o identificador.
     *
     * Quem lê um item para mostrar os RELACIONAMENTOS dos relacionados — a tabela da ficha, e o
     * Kanban de um relacionamento — precisa de 2. Sem isso o valor chega como id cru, porque o
     * `propertyValue` não acha o objeto no `_rels` e cai no campo.
     */
    depth?: number
    /**
     * Quais propriedades vêm no corpo de cada item. Ausente é todas.
     *
     * Serve a quem pede uma leitura pelo que vem AO LADO dos itens — hoje o quadro, que busca os
     * candidatos das dimensões (`rel-domains`) quando já tem os itens em mão. Com projeção
     * mínima o corpo volta praticamente vazio, e a resposta é só a lista de candidatos.
     */
    properties?: Array<string>
}

class InstanceService {
    async list({systemAlias, ideaAlias}: SystemIdeaAlias, config: Config) {
        return await serve(async () => await service.get(
            `systems/${systemAlias}/ideas/${ideaAlias}/instances${mountQuery(config)}`
        ))
    }

    async get({systemAlias, ideaAlias, instanceId}: SystemIdeaInstanceAlias, config: Config) {
        return await serve(async () => await service.get(
            `systems/${systemAlias}/ideas/${ideaAlias}/instances/${instanceId}${mountQuery(config)}`
        ))
    }

    /**
     * As calculadas de um item EM EDIÇÃO, calculadas pelo servidor sobre o rascunho — sem gravar.
     *
     * É o que o formulário chama ao sair de um campo: quem calcula é o servidor, com o mesmo motor
     * da leitura, e o cliente não executa nada do que o designer escreveu. Sem `instanceId` é um
     * item novo.
     *
     * `defaulted`: os campos que o formulário ainda deixa com o padrão por fórmula — o servidor os põe no
     * item (`defaults`) antes de calcular o resto.
     */
    async preview({systemAlias, ideaAlias, instanceId}: {systemAlias: string, ideaAlias: string, instanceId?: string | number},
                  draft: any, defaulted: string[] = []): Promise<{computed: Record<string, any>, errors: Record<string, string>,
                      /** O padrão por fórmula de cada campo de `defaulted`, na forma gravada */
                      defaults?: Record<string, any>,
                      /** O que quem pede poderá fazer com o item depois de gravado — só com fórmula de acesso */
                      access?: {visible: boolean, editable: boolean}}> {
        const item = instanceId ? `/${instanceId}` : ''
        const query = defaulted.length ? `?defaulted=${defaulted.map(encodeURIComponent).join(',')}` : ''
        return await serve(async () => await service.post(
            `systems/${systemAlias}/ideas/${ideaAlias}/instances${item}/preview${query}`, draft))
    }

    async add({systemAlias, ideaAlias}: SystemIdeaAlias, instance: any) {
        return await serve(async () => await service.post(`systems/${systemAlias}/ideas/${ideaAlias}/instances`, instance))
    }

    async update({systemAlias, ideaAlias, instanceId}: SystemIdeaInstanceAlias, changes: any) {
        return await serve(async () => await service.patch(`systems/${systemAlias}/ideas/${ideaAlias}/instances/${instanceId}`, changes))
    }

    /** A situação vai pelo id, que é o que ela passou a ter no lugar do alias. */
    async updateState({systemAlias, ideaAlias, instanceId}: SystemIdeaInstanceAlias, stateId: number) {
        return await serve(async () => await service.put(`systems/${systemAlias}/ideas/${ideaAlias}/instances/${instanceId}/state`, {value: stateId}))
    }

    /** Prévia do impacto de excluir: quem perde o vínculo e quem impede. */
    async dependencies({systemAlias, ideaAlias, instanceId}: SystemIdeaInstanceAlias) {
        return await serve(async () => await service.get(
            `systems/${systemAlias}/ideas/${ideaAlias}/instances/${instanceId}/dependencies`
        ))
    }

    async delete({systemAlias, ideaAlias, instanceId}: SystemIdeaInstanceAlias) {
        return await serve(async () => await service.delete(`systems/${systemAlias}/ideas/${ideaAlias}/instances/${instanceId}`))
    }
}

export default InstanceService